"use client"

import { useEffect, useRef, useState, useCallback } from "react"
import { motion, AnimatePresence } from "framer-motion"

type CursorState = "default" | "hover" | "view" | "hidden" | "text"

interface CursorConfig {
  state: CursorState
  label?: string
}

// Check if device supports hover (not touch-only)
const isTouchDevice = () => {
  if (typeof window === "undefined") return true
  return "ontouchstart" in window || navigator.maxTouchPoints > 0
}

// Sunburst cursor with radiating rays
function SunburstCursor({ isHover, isPressed }: { isHover: boolean; isPressed: boolean }) {
  const rayCount = 16
  const rays = Array.from({ length: rayCount }, (_, i) => i)

  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        filter: isHover ? "drop-shadow(0 0 4px rgba(201, 162, 39, 0.5))" : "none",
      }}
    >
      {/* Radiating rays */}
      {rays.map((i) => {
        const angle = (i * 360) / rayCount
        const innerRadius = isHover ? 5 : 4
        const outerRadius = isHover ? 14 : 11
        const radians = (angle * Math.PI) / 180
        const x1 = 16 + Math.cos(radians) * innerRadius
        const y1 = 16 + Math.sin(radians) * innerRadius
        const x2 = 16 + Math.cos(radians) * outerRadius
        const y2 = 16 + Math.sin(radians) * outerRadius

        // Alternating ray lengths for visual interest
        const isLong = i % 2 === 0
        const finalX2 = isLong ? x2 : 16 + Math.cos(radians) * (outerRadius * 0.7)
        const finalY2 = isLong ? y2 : 16 + Math.sin(radians) * (outerRadius * 0.7)

        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={finalX2}
            y2={finalY2}
            stroke="var(--color-accent-gold, #c9a227)"
            strokeWidth={isHover ? 1.5 : 1}
            strokeLinecap="round"
            opacity={isPressed ? 1 : isLong ? 0.9 : 0.6}
          />
        )
      })}

      {/* Center circle */}
      <circle
        cx="16"
        cy="16"
        r={isPressed ? 4 : isHover ? 3.5 : 3}
        fill="var(--color-accent-gold, #c9a227)"
        opacity={isPressed ? 1 : 0.9}
      />

      {/* Inner glow on hover */}
      {isHover && (
        <circle cx="16" cy="16" r="6" fill="var(--color-accent-gold, #c9a227)" opacity="0.15" />
      )}
    </svg>
  )
}

export default function CursorFollower() {
  const cursorRef = useRef<HTMLDivElement>(null)
  const [mounted, setMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [config, setConfig] = useState<CursorConfig>({ state: "default" })
  const [isPressed, setIsPressed] = useState(false)

  // Prevent hydration mismatch - always render same content on server/client initially
  useEffect(() => {
    setMounted(true)
  }, [])

  const getCursorConfig = useCallback((target: HTMLElement): CursorConfig => {
    const cursorType = target.closest("[data-cursor]")?.getAttribute("data-cursor")
    const cursorLabel = target.closest("[data-cursor-label]")?.getAttribute("data-cursor-label")

    if (cursorType === "hidden") {
      return { state: "hidden" }
    }

    if (cursorType === "view" || cursorLabel) {
      return { state: "view", label: cursorLabel || "View" }
    }

    if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
      return { state: "text" }
    }

    const isInteractive =
      target.tagName === "A" ||
      target.tagName === "BUTTON" ||
      target.closest("a") !== null ||
      target.closest("button") !== null ||
      target.closest('[role="button"]') !== null ||
      getComputedStyle(target).cursor === "pointer"

    if (isInteractive) {
      const isProjectCard = target.closest("[data-project]") || target.closest(".work-card")
      if (isProjectCard) {
        return { state: "view", label: "View" }
      }
      return { state: "hover" }
    }

    return { state: "default" }
  }, [])

  useEffect(() => {
    // Skip on touch devices
    if (!mounted || isTouchDevice()) {
      return
    }

    const cursor = cursorRef.current
    if (!cursor) return

    // Hide system cursor globally
    document.body.style.cursor = "none"
    const style = document.createElement("style")
    style.id = "sunburst-cursor-style"
    style.textContent = `
      *, *::before, *::after {
        cursor: none !important;
      }
    `
    document.head.appendChild(style)

    const handleMouseMove = (e: MouseEvent) => {
      // Direct DOM update for zero-lag movement
      cursor.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`

      if (!isVisible) setIsVisible(true)

      const target = e.target as HTMLElement
      const newConfig = getCursorConfig(target)
      setConfig(newConfig)
    }

    const handleMouseLeave = () => {
      setIsVisible(false)
    }

    const handleMouseDown = () => {
      setIsPressed(true)
    }

    const handleMouseUp = () => {
      setIsPressed(false)
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseleave", handleMouseLeave)
    document.addEventListener("mousedown", handleMouseDown)
    document.addEventListener("mouseup", handleMouseUp)

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseleave", handleMouseLeave)
      document.removeEventListener("mousedown", handleMouseDown)
      document.removeEventListener("mouseup", handleMouseUp)
      document.body.style.cursor = ""
      const styleEl = document.getElementById("sunburst-cursor-style")
      if (styleEl) styleEl.remove()
    }
  }, [mounted, isVisible, getCursorConfig])

  // Render nothing until mounted (prevents hydration mismatch)
  // Also hide on touch devices
  if (!mounted || isTouchDevice()) return null

  const isHover = config.state === "hover" || config.state === "view"
  const hasLabel = config.state === "view" && config.label

  return (
    <div
      ref={cursorRef}
      className="pointer-events-none fixed top-0 left-0 z-[9999] hidden md:block will-change-transform"
      style={{
        opacity: isVisible && config.state !== "hidden" ? 1 : 0,
        transition: "opacity 0.2s ease",
      }}
    >
      {/* Sunburst cursor centered on mouse position */}
      <motion.div
        style={{
          x: "-50%",
          y: "-50%",
        }}
        animate={{
          rotate: [0, 360],
          scale: isPressed ? 0.8 : isHover ? 1.15 : 1,
        }}
        transition={{
          rotate: {
            duration: isHover ? 8 : 20,
            repeat: Infinity,
            ease: "linear",
          },
          scale: {
            type: "spring",
            stiffness: 400,
            damping: 25,
          },
        }}
      >
        <SunburstCursor isHover={isHover} isPressed={isPressed} />
      </motion.div>

      {/* Label bubble for "View" state */}
      <AnimatePresence mode="wait">
        {hasLabel && (
          <motion.div
            key={config.label}
            className="absolute top-4 left-4 px-3 py-1.5 rounded-full"
            style={{
              backgroundColor: "rgba(201, 162, 39, 0.9)",
              backdropFilter: "blur(8px)",
            }}
            initial={{ opacity: 0, scale: 0.8, y: 5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 5 }}
            transition={{ duration: 0.15 }}
          >
            <span
              className="text-[10px] font-semibold tracking-wider uppercase"
              style={{
                fontFamily: "var(--font-mono)",
                color: "#0f0d0a",
              }}
            >
              {config.label}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Text cursor - I-beam */}
      {config.state === "text" && (
        <motion.div
          className="absolute"
          style={{
            width: 2,
            height: 20,
            backgroundColor: "var(--color-accent-gold, #c9a227)",
            left: -1,
            top: -10,
          }}
          animate={{
            opacity: [1, 0, 1],
          }}
          transition={{
            opacity: { duration: 1, repeat: Infinity },
          }}
        />
      )}
    </div>
  )
}
